// pages/TeamCalendarPage.jsx
// Part 2 of CALENDAR-WHATSAPP-SCOPE.md — director-level read-only view of the whole
// team's diaries in one place. Owner/manager only (route also wrapped in ModuleGate
// module="scheduling" in App.js). Deliberately a hand-built plain CSS grid, not
// FullCalendar's paid Premium resource view (confirmed decision, 15 Aug 2026) — no
// drag/drop, no conflict/double-booking logic, matches how KanbanBoard.js/
// LeadsKanbanBoard.js were hand-built rather than pulling in a board library.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useMobile } from '../hooks/useMobile';

const token = () => localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatWeekRange(weekStart) {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${fmt(weekStart)} – ${fmt(end)}, ${end.getFullYear()}`;
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

const cardStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10 };
const btnGhost = { background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 12px', fontSize: 13, cursor: 'pointer', fontWeight: 600, color: '#374151' };

export default function TeamCalendarPage() {
  const mobile = useMobile();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isOwnerOrManager = user.brokerLevel === 'owner' || user.brokerLevel === 'manager' || user.role === 'platform_owner';

  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [advisers, setAdvisers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOwnerOrManager) navigate('/dashboard', { replace: true });
  }, [isOwnerOrManager, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    try {
      const params = new URLSearchParams({ start: weekStart.toISOString(), end: weekEnd.toISOString() });
      const res = await fetch(`/api/broker/team-calendar?${params.toString()}`, { headers: authHeaders() });
      if (res.status === 403) { setError("You don't have access to the team calendar."); setAdvisers([]); setEvents([]); return; }
      const data = await res.json();
      setAdvisers(Array.isArray(data.advisers) ? data.advisers : []);
      setEvents(Array.isArray(data.events) ? data.events : []);
    } catch {
      setError('Failed to load the team calendar.');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { if (isOwnerOrManager) load(); }, [isOwnerOrManager, load]);

  if (!isOwnerOrManager) return null;

  // events grouped by [userId][dayIndex 0-6] for cell lookup while rendering.
  const grouped = {};
  for (const ev of events) {
    const dayIdx = Math.floor((new Date(ev.start_time) - weekStart) / 86400000);
    if (dayIdx < 0 || dayIdx > 6) continue;
    grouped[ev.user_id] = grouped[ev.user_id] || Array.from({ length: 7 }, () => []);
    grouped[ev.user_id][dayIdx].push(ev);
  }

  return (
    <div className="broker-dashboard">
      <Sidebar />
      <main className="main-content" style={mobile ? {} : { padding: '28px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0e1b2c' }}>Team Calendar</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#718096' }}>
              Real availability from each adviser's connected calendar. Read-only — nothing here can be booked or moved.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button style={btnGhost} onClick={() => setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}>← Prev</button>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: '#374151', minWidth: 160, textAlign: 'center' }}>{formatWeekRange(weekStart)}</span>
            <button style={btnGhost} onClick={() => setWeekStart(getMonday(new Date()))}>This week</button>
            <button style={btnGhost} onClick={() => setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}>Next →</button>
          </div>
        </div>

        {error && <div style={{ color: '#b91c1c', marginBottom: 12, fontSize: 13 }}>{error}</div>}

        {loading ? (
          <div style={{ color: '#718096', fontSize: 13.5 }}>Loading…</div>
        ) : advisers.length === 0 ? (
          <div style={{ ...cardStyle, padding: 24, color: '#718096', fontSize: 13.5 }}>No advisers to show.</div>
        ) : (
          <div style={{ ...cardStyle, overflowX: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `110px repeat(${advisers.length}, minmax(170px, 1fr))`, minWidth: 110 + advisers.length * 170 }}>
              {/* Header row */}
              <div style={{ padding: '12px 10px', borderBottom: '2px solid #e2e8f0', borderRight: '1px solid #e2e8f0', fontSize: 12, fontWeight: 700, color: '#a0aec0' }} />
              {advisers.map((a) => (
                <div key={a.id} style={{ padding: '12px 10px', borderBottom: '2px solid #e2e8f0', borderRight: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0e1b2c' }}>{a.name}</div>
                  {a.connected ? (
                    <div style={{ fontSize: 11, color: '#10b981', fontWeight: 600, marginTop: 2 }}>● {a.provider}</div>
                  ) : (
                    <div style={{ fontSize: 11, color: '#a0aec0', fontWeight: 600, marginTop: 2 }}>○ Not connected</div>
                  )}
                </div>
              ))}

              {/* Day rows */}
              {DAY_LABELS.map((label, dayIdx) => {
                const dayDate = new Date(weekStart);
                dayDate.setDate(dayDate.getDate() + dayIdx);
                const isToday = new Date().toDateString() === dayDate.toDateString();
                return (
                  <React.Fragment key={label}>
                    <div style={{
                      padding: '10px', borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #e2e8f0',
                      fontSize: 12, fontWeight: 700, color: isToday ? '#10b981' : '#718096',
                      background: isToday ? '#f0fdf4' : 'transparent',
                    }}>
                      {label} {dayDate.getDate()}
                    </div>
                    {advisers.map((a) => {
                      const dayEvents = (grouped[a.id] || [])[dayIdx] || [];
                      return (
                        <div key={a.id + dayIdx} style={{
                          padding: '8px', borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9',
                          background: isToday ? '#f9fffb' : 'transparent', minHeight: 34,
                        }}>
                          {!a.connected ? (
                            <span style={{ fontSize: 11.5, color: '#cbd5e0' }}>—</span>
                          ) : dayEvents.length === 0 ? (
                            <span style={{ fontSize: 11.5, color: '#cbd5e0' }}>Free</span>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {dayEvents.map((ev) => (
                                <div key={ev.id} title={ev.title || 'Busy'} style={{
                                  fontSize: 11, background: '#dbeafe', color: '#1e40af', borderRadius: 5,
                                  padding: '3px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                  {ev.is_all_day ? 'All day' : formatTime(ev.start_time)} {ev.title ? `· ${ev.title}` : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
